// Keep distances from the nearest edge in millimetres. Add/remove only at centre.
export function centreAxis(source, target, segments = 128) {
  if (!(source > 0 && target > 0)) throw new Error("Invalid milling dimensions");
  const half = Math.min(source, target) / 2;
  const count = Math.max(1, Math.ceil(segments / 2));
  const axis = [];
  for (let i=0; i<=count; i++) {
    const distance=half*i/count;
    axis.push({ source: distance/source, target: distance/target });
  }
  for (let i=0; i<=count; i++) {
    const distance=half*(1-i/count);
    axis.push({ source: 1-distance/source, target: 1-distance/target });
  }
  // At equal size the two centre samples are identical.
  return axis.filter((p,i) => !i || p.source!==axis[i-1].source || p.target!==axis[i-1].target);
}

export function parseStretch(spec) {
  if (spec?.includes(";")) {
    const intervals=spec.split(";").map(s=>parseStretch(s).interval);
    if(spec.length>255 || intervals.length>16 || intervals.some((r,i)=>!r || (i>0 && r[0]<=intervals[i-1][1])))
      throw new Error("Különálló, növekvő intervallumokat adj meg: 10:30;70:90.");
    return {intervals};
  }
  if (!spec?.trim()) return { points: [0.5] };
  const interval = spec.includes(":");
  const parts = spec.split(interval ? ":" : ",");
  const values = parts.map((v) => v.trim() ? Number(v) / 100 : NaN);
  if (spec.length > 255 || (interval ? values.length !== 2 : values.length > 16) ||
      values.some((v, i) => !Number.isFinite(v) || v < 0 || v > 1 ||
        (!interval && (v === 0 || v === 1)) || (i > 0 && v <= values[i - 1]))) {
    throw new Error("A nyújtási pontok 0 és 100% között, növekvő sorrendben legyenek (pl. 25,75), vagy adj meg intervallumot (pl. 25:75).");
  }
  return interval ? { interval: values } : { points: values };
}

// Build piecewise linear UV/geometry mapping. Fixed sections retain their mm size.
export function stretchAxis(source, target, segments = 128, spec = "", repeatSpec = "") {
  if (!(source > 0 && target > 0 && Number.isFinite(source) && Number.isFinite(target)))
    throw new Error("Invalid milling dimensions");
  if (repeatSpec?.trim()) return repeatAxis(source, target, segments, repeatSpec);
  const { points, interval, intervals } = parseStretch(spec);
  if (points?.length === 1 && points[0] === 0.5) return centreAxis(source, target, segments);
  const delta = target - source;
  const anchors = [{ source: 0, target: 0 }];
  if (interval || intervals) {
    const ranges=intervals || [interval], share=delta/ranges.length;
    ranges.forEach((range,i)=>{
      const [a,b]=range.map(v=>v*source);
      if(b-a+share<0) throw new Error("A méret túl kicsi a megadott nyújtási intervallumhoz.");
      anchors.push({source:a,target:a+i*share},{source:b,target:b+(i+1)*share});
    });
  } else {
    const share = delta / points.length;
    for (let i = 0; i < points.length; i++) {
      const centre = points[i] * source;
      const a = centre + Math.min(0, share / 2);
      const b = centre - Math.min(0, share / 2);
      if (a < anchors.at(-1).source || b > source)
        throw new Error("A méret túl kicsi a megadott nyújtási pontokhoz.");
      anchors.push({ source: a, target: a + i * share },
        { source: b, target: b + (i + 1) * share });
    }
  }
  anchors.push({ source, target });
  const result = [];
  for (let i = 1; i < anchors.length; i++) {
    const a = anchors[i - 1], b = anchors[i];
    const count = Math.max(1, Math.ceil((b.source - a.source) / source * segments));
    // Removed sections have coincident target coordinates; keep only their endpoints.
    const steps = a.target === b.target ? 1 : count;
    for (let j = 0; j <= steps; j++) {
      const ratio = j / steps;
      const p = { source: (a.source + (b.source - a.source) * ratio) / source,
        target: (a.target + (b.target - a.target) * ratio) / target };
      const last = result.at(-1);
      if (!last || p.source !== last.source || p.target !== last.target) result.push(p);
    }
  }
  return result;
}

// Both intervals use source percentages: tile start:end @ repeated area start:end.
export function parseRepeat(spec) {
  if (!spec?.trim()) return null;
  if(spec.includes(";")) {
    const sectors=spec.split(";").map(s=>parseRepeat(s));
    if(spec.length>255 || sectors.length>16 || sectors.some((s,i)=>!s || (i>0 && s.area[0]<=sectors[i-1].area[1])))
      throw new Error("Az ismétlési tartományok növekvő sorrendben, átfedés nélkül legyenek.");
    return {sectors};
  }
  const parts = spec.split("@");
  if (spec.length > 255 || parts.length !== 2 || parts.some(p => !p.trim()))
    throw new Error("Az ismétléshez add meg a mintaszakaszt és a kitöltendő intervallumot is.");
  const ranges = parts.map(p => parseStretch(p).interval);
  if (ranges.some(r => !r)) throw new Error("Az ismétlés mindkét tartománya intervallum legyen (pl. 40:60 és 20:80).");
  return { tile: ranges[0], area: ranges[1] };
}

export function repeatAxis(source, target, segments, spec) {
  const config=parseRepeat(spec);
  if(config.sectors) {
    const result=[], share=(target-source)/config.sectors.length;
    let previous=0,offset=0;
    config.sectors.forEach(({tile,area})=>{
      const a=area[0]*source,b=area[1]*source,length=b-a+share;
      if(length<=1e-8) throw new Error("A méret túl kicsi az ismétlési szektorhoz.");
      result.push({source:previous/source,target:(previous+offset)/target},{source:a/source,target:(a+offset)/target});
      // Reuse the centred single-sector mapping; discard its fixed outer frame.
      const axis=repeatAxis(source,source+share,segments,`${tile[0]*100}:${tile[1]*100}@${area[0]*100}:${area[1]*100}`);
      const start=axis.findIndex(p=>Math.abs(p.target*(source+share)-a)<1e-7);
      let end=axis.length-1;
      while(end>=0 && Math.abs(axis[end].target*(source+share)-(b+share))>1e-7)end--;
      result.push(...axis.slice(start,end+1).map(p=>({source:p.source,target:(p.target*(source+share)+offset)/target})));
      offset+=share;previous=b;
    });
    result.push({source:previous/source,target:(previous+offset)/target},{source:1,target:1});
    return result;
  }
  const { tile, area } = parseRepeat(spec);
  const tileStart = tile[0] * source, tileLength = (tile[1] - tile[0]) * source;
  const start = area[0] * source, end = target - (1 - area[1]) * source;
  const length = end - start;
  if (length <= 1e-8) throw new Error("A méret túl kicsi: az ismétlésen kívüli keretek nem férnek el.");
  if (length / tileLength > 256) throw new Error("Túl sok ismétlés: válassz hosszabb mintaszakaszt (legfeljebb 256 ismétlés).");
  const result = [];
  const span = (s0, s1, t0, t1) => {
    const steps = Math.max(1, Math.ceil(Math.abs(s1 - s0) / source * segments));
    for (let i=0; i<=steps; i++) {
      const p = { source: Math.max(0, Math.min(1, (s0 + (s1 - s0) * i / steps) / source)),
        target: (t0 + (t1 - t0) * i / steps) / target };
      const last = result.at(-1);
      if (!last || Math.abs(p.source - last.source) > 1e-12 || Math.abs(p.target - last.target) > 1e-12) result.push(p);
    }
  };
  if (start > 0) span(0, start, 0, start);
  // Keep one tile centred as sizes change; add matching neighbours on both sides.
  // An odd tile count keeps the centre phase stable, with equal crops at the edges.
  const tiles = 1 + 2 * Math.max(0, Math.ceil((length / tileLength - 1) / 2 - 1e-10));
  const origin = start - (tiles * tileLength - length) / 2;
  for (let i=0; i<tiles; i++) {
    const tileOrigin = origin + i * tileLength;
    const left = Math.max(start, tileOrigin), right = Math.min(end, tileOrigin + tileLength);
    if (right - left > 1e-8) span(tileStart + left - tileOrigin, tileStart + right - tileOrigin, left, right);
  }
  if (end < target) span(area[1] * source, source, end, target);
  return result;
}
